import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  ConfigurationsService,
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys,
  PBasketConfigurations,
} from '@epicuro-next/platform/configuration';
import { ProductExpandDetailsContentType } from '@epicuro-next/product-card';
import {
  activateConfigStrip,
  ConfigStripActivationSource,
} from '@epicuro-next/state/config-strip-state';
import {
  selectAllProductsLoaded,
  selectCollapse,
  selectProductBasketSearchQuery,
  selectProductsLoading,
  selectProductTitleFieldsRule,
  selectResultCount,
} from '@epicuro-next/state/p-basket-state';
import {
  PBasketView,
  selectPBasketView,
} from '@epicuro-next/state/user-ui-state';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { ProductCardExpandContainerComponent } from 'libs/product-card/src/lib/product-card-expand/product-card-expand-container/product-card-expand-container.component';
import { MediaRelationsSuggestResponse } from 'models-media-relations';
import { combineLatest, Subject, tap } from 'rxjs';
import { map, shareReplay, take, takeUntil } from 'rxjs/operators';
/**
 * Product list component
 */
@Component({
  selector: 'epc-products-search-list',
  templateUrl: './products-search-list.component.html',
  styleUrls: ['./products-search-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductsSearchListComponent implements OnDestroy, OnInit {
  @Input()
  public products: Product[];

  @Input() public hideProducts = false;

  @Input() public showActions = true;

  @Input() public showBookmarkIcon = true;

  @Input() public showPagination = true;

  @Input() public autoOpenProduct = true;

  @Input() public selectedProductIndexes!: number[];

  @Input() public loading = false;

  // tslint:disable-next-line:prefer-optional
  @Input() public groupVariantsOverride!: undefined | boolean;

  @Input() public isSearchV2Enabled = false;

  @Input() public expandProductCardActive = false;

  @Input() public mediaRelations: MediaRelationsSuggestResponse[];
  @Input() public expandUnavailableProductActive = false;

  @Output()
  public scrolled: EventEmitter<void> = new EventEmitter();

  @Output()
  public cardClicked: EventEmitter<{ product: Product; index: number }> =
    new EventEmitter();

  @ViewChildren(ProductCardExpandContainerComponent)
  public expandContainerList: QueryList<ProductCardExpandContainerComponent>;

  public closeAllExpandedProductCards() {
    this.expandContainerList.forEach((expandContainerComponent) => {
      expandContainerComponent.isKeepOpen.set(false);
      expandContainerComponent.productCard()?.showSimilarProducts.set(false);
    });
  }

  public readonly productExpandDetailsContentType =
    ProductExpandDetailsContentType;

  public groupingEnabled$ = this.configsService
    .getConfigValue<
      PBasketConfigurations,
      PBasketConfigurationKeys.ShowLayoutSwitcher
    >({
      namespace: NAMESPACE_PRODUCT_BASKET,
      featureName: PBasketConfigurationKeys.ShowLayoutSwitcher,
      defaultValue: false,
    })
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public skeletons = new Array(25).fill('');
  public isGridView$ = combineLatest([
    this.store.pipe(select(selectPBasketView)),
    atxIsMobile$,
    this.groupingEnabled$,
  ]).pipe(
    map(
      ([view, isMobile, groupingEnabled]: [PBasketView, boolean, boolean]) => {
        if (!isMobile || !groupingEnabled) {
          return false;
        }

        return view === PBasketView.Grid;
      },
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  public groupVariants$ = this.store.pipe(
    select(selectCollapse),
    map((collapse) => this.groupVariantsOverride ?? collapse),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Component destroy subject */
  private destroy$: Subject<void> = new Subject();

  /**
   * Emit when a scroll is performed
   *
   * @private
   */
  private scroll$ = new Subject<void>();

  public productsLoading$ = this.store.pipe(
    select(selectProductsLoading),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public totalProducts$ = this.store.pipe(
    select(selectResultCount),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly titleFields$ = this.store.select(
    selectProductTitleFieldsRule,
  );

  public allProductsLoaded$ = this.store.pipe(select(selectAllProductsLoaded));

  /** provides dependencies */
  constructor(
    protected store: Store,
    protected location: Location,
    private configsService: ConfigurationsService,
  ) {}

  public ngOnInit(): void {
    this.setScrollListener();
  }

  public ngOnChanges({ mediaRelations }: SimpleChanges) {
    setTimeout(() => {
      if (mediaRelations) {
        this.expandContainerList?.forEach((expandContainer) => {
          expandContainer.setExpandAnimationVariables;
        });
      }
    }, 900);
  }

  /**
   * When the product card is clicked, it redirects to config strip
   */
  public clickedProductCard(product: Product, index: number) {
    this.cardClicked.emit({ product, index });

    if (this.autoOpenProduct) {
      this.store
        .pipe(select(selectProductBasketSearchQuery))
        .pipe(
          take(1),
          tap((search) => {
            this.store.dispatch(
              activateConfigStrip({
                product,
                index,
                source: {
                  source: ConfigStripActivationSource.Search,
                  details: {
                    query: search ?? '',
                  },
                },
              }),
            );
          }),
        )
        .subscribe();
    }
  }

  /**
   * Checks if the products have been loaded
   * and if they have, it increments the page number
   * and fetches the products.
   * Also fires on component init
   */
  public onScroll() {
    this.productsLoading$.pipe(take(1)).subscribe((loading) => {
      if (!loading) {
        this.scroll$.next();
      }
    });
  }

  private setScrollListener() {
    this.scroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.scrolled.emit();
    });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isSelected(i: number) {
    return this.selectedProductIndexes?.includes(i);
  }
}
